import { useEffect, useState } from "react";

// mui components
import { Accordion, AccordionSummary, AccordionDetails, Typography, Box, IconButton, Checkbox, FormControlLabel } from "@mui/material";

// mui icons
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

// redux
import { useDispatch, useSelector } from "react-redux";
import { loadScoreDistributionGroupsOverallState, loadScoreDistributionGroupsAdditionalState, loadChartScaleResetState } from "store/features/analysisResultsSlice";

// props validations
import PropTypes from "prop-types";

function DataPointSampling({ likertScaleData = [], scoreDistributionSelectedVariable = {}, moduleName = "", data = {}, setData = () => { } }) {
    const dispatch = useDispatch();

    const [isFilterOpen, setIsFilterOpen] = useState(false);
    const scaleValue = likertScaleData[scoreDistributionSelectedVariable?.value] ?? 0;
    const chartScaleReset = useSelector((state) => state.analysisResultsStore.chartScaleReset);

    const createOptions = (max, initial) => {
        const options = {};
        if (initial) {
            for (let i = 1; i <= max; i++) {
                options[i] = false;
            }
        } else {
            const array = Array.from({ length: scaleValue }, (_, i) => ++i);
            const selectedOptions = Object.values(data).flatMap((innerObj) =>
                Object.entries(innerObj)
                    .filter(([, value]) => value)
                    .map(([key]) => +key)
            );

            const filteredOptions = array.filter((item) => !selectedOptions.includes(item));
            filteredOptions.forEach((datum) => (options[datum] = false));
        }

        return options;
    };

    // const [data, setData] = useState({});

    useEffect(() => {
        // Close dropdown and reset data when a global reset is triggered
        if (chartScaleReset === false) {
            setIsFilterOpen(false);
            setData({});
            // restore the flag so other components can react normally
            dispatch(loadChartScaleResetState(true));
            return;
        }
        if (isFilterOpen && Object.keys(data).length === 0) {
            setData((prev) => ({
                ...prev,
                1: createOptions(scaleValue, true),
            }));
        }
        if (!isFilterOpen) {
            const result = Object.values(data).map((obj) =>
                Object.keys(obj)
                    .filter((key) => obj[key])
                    .join(",")
            );
            if (result.some(Boolean)) {
                if (moduleName === "overall") {
                    dispatch(loadScoreDistributionGroupsOverallState(result));
                } else {
                    dispatch(loadScoreDistributionGroupsAdditionalState(result));
                }
            } else {
                if (moduleName === "overall") {
                    dispatch(loadScoreDistributionGroupsOverallState([]));
                } else {
                    dispatch(loadScoreDistributionGroupsAdditionalState([]));
                }
            }
        }
    }, [data, dispatch, isFilterOpen, scaleValue, moduleName]);

    const handleOpen = () => {
        setIsFilterOpen(!isFilterOpen);
    };

    const handleOnChange = (group, option) => {
        // Check if the option is already truthy in any other group (skip current group by key)
        let existsTruthyElsewhere = false;
        for (const [key, obj] of Object.entries(data)) {
            if (String(key) === String(group)) continue;
            if (option in obj && obj[option]) {
                existsTruthyElsewhere = true;
                break;
            }
        }
        if (!existsTruthyElsewhere) {
            setData((prev) => {
                const prevGroup = prev[group] || {};
                const newGroup = {
                    ...prevGroup,
                    [option]: !prevGroup[option],
                };
                const hasAnyTrue = Object.values(newGroup).some(Boolean);
                if (hasAnyTrue) {
                    return {
                        ...prev,
                        [group]: newGroup,
                    };
                }
                // remove the group entirely if no truthy values remain
                const next = { ...prev };
                delete next[group];
                return next;
            });
        }
    };

    const handleAddNewGroup = () => {
        const options = createOptions(scaleValue, false);
        if (Object.keys(options).length !== 0) {
            setData((prev) => {
                const lastKey = Math.max(...Object.keys(prev).map(Number));
                const nextKey = Number.isFinite(lastKey) ? lastKey + 1 : 1;
                if (prev[lastKey] && Object.values(prev[lastKey]).some(Boolean)) {
                    return {
                        ...prev,
                        [nextKey]: options,
                    };
                }
                return { ...prev };
            });
        }
    };

    const getSelectedValuesSubString = (val) => {
        const selectedValues = Object.entries(val)
            .filter(([, value]) => value)
            .map(([key]) => Number(key));
        return selectedValues.length ? `(${selectedValues.join(", ")})` : "";
    };

    return (
        <div style={{ position: "relative" }}>
            <IconButton
                onClick={handleOpen}
                sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    border: !scoreDistributionSelectedVariable?.value ? "1px solid #ced4da" : "1px solid #47c8ff",
                    borderRadius: "5px",
                    fontSize: 11,
                    padding: "5px 15px",
                    color: "#005eef",
                }}
                disabled={!scoreDistributionSelectedVariable?.value}
            >
                Data Point Sampling
                <ExpandMoreIcon
                    sx={{
                        fontSize: 20,
                        transition: "0.3s",
                        transform: isFilterOpen ? "rotate(180deg)" : "rotate(0deg)",
                    }}
                />
            </IconButton>
            {isFilterOpen && (
                <Box
                    sx={{
                        position: "absolute",
                        top: 34,
                        right: 0,
                        left: "50%",
                        transform: "translate(-50%, 0%)",
                        border: "1px solid #47c8ff",
                        borderRadius: "5px",
                        bgcolor: "#FFFFFF",
                        width: "172px",
                        padding: "10px 0px",
                        zIndex: 999,
                    }}
                >
                    {Object.entries(data).map(([key, value], i) => (
                        <Accordion
                            key={i}
                            disableGutters
                            sx={{
                                boxShadow: "none",
                                "&:before": { display: "none" },
                                marginBottom: "5px",
                            }}
                        >
                            <AccordionSummary
                                expandIcon={
                                    <Typography
                                        sx={{
                                            paddingTop: "2px",
                                            fontWeight: "bold",
                                            fontSize: "16px",
                                            lineHeight: "20px",
                                            userSelect: "none",
                                            color: "#005eef",
                                        }}
                                    >
                                        +
                                    </Typography>
                                }
                                sx={{
                                    minHeight: "0px",
                                    "& .MuiAccordionSummary-content": {
                                        margin: 0,
                                    },
                                }}
                            >
                                <Typography
                                    sx={{
                                        fontSize: "12px",
                                        color: "#005eef",
                                    }}
                                >
                                    {`Group ${key} ${getSelectedValuesSubString(value)}`}
                                </Typography>
                            </AccordionSummary>

                            <AccordionDetails
                                sx={{
                                    display: "flex",
                                    flexDirection: "column",
                                    marginLeft: "16px",
                                }}
                            >
                                {Object.entries(value).map(([label, checked], j) => {
                                    return (
                                        <FormControlLabel
                                            key={j}
                                            sx={{
                                                "& .MuiTypography-root": {
                                                    fontSize: 12,
                                                    margin: "2px 0px",
                                                    padding: 0,
                                                    display: "block",
                                                    paddingLeft: 1,
                                                    color: "#005eef",
                                                },
                                            }}
                                            control={
                                                <Checkbox
                                                    sx={{
                                                        "& .MuiSvgIcon-root": {
                                                            fontSize: 18,
                                                            padding: 0,
                                                            strokeWidth: 1,
                                                        },
                                                    }}
                                                    style={{
                                                        padding: 3,
                                                    }}
                                                    color="primary"
                                                    onChange={() => handleOnChange(key, label, i)}
                                                    value={label}
                                                    checked={checked}
                                                />
                                            }
                                            label={label}
                                        />
                                    );
                                })}
                            </AccordionDetails>
                        </Accordion>
                    ))}
                    <div
                        style={{
                            display: "flex",
                            justifyContent: "space-between",
                            padding: "0px 16px",
                        }}
                    >
                        <p
                            style={{
                                fontSize: "12px",
                                color: "#005eef",
                                margin: "0px",
                            }}
                        >
                            Add Group
                        </p>
                        <IconButton
                            variant="text"
                            disableRipple
                            sx={{
                                backgroundColor: "transparent",
                                "&:hover": {
                                    backgroundColor: "transparent",
                                },
                                "&:active": {
                                    backgroundColor: "transparent",
                                },
                                "&.Mui-focusVisible": {
                                    backgroundColor: "transparent",
                                },
                            }}
                            style={{
                                fontWeight: "bold",
                                fontSize: "16px",
                                color: "#005eef",
                                margin: "0px",
                                padding: "0px",
                            }}
                            onClick={handleAddNewGroup}
                        >
                            +
                        </IconButton>
                    </div>
                </Box>
            )}
        </div>
    );
}

// Typechecking props for the DataPointSampling
DataPointSampling.propTypes = {
    likertScaleData: PropTypes.array,
    scoreDistributionSelectedVariable: PropTypes.object,
    moduleName: PropTypes.string,
    data: PropTypes.object,
    setData: PropTypes.func,
};

export default DataPointSampling;
